import {SLQueryParamConfig} from '#/contexts/query-params-provider';
import {useQueryParamsTabKey} from '#/hooks/use-query-params-tab';
import {ClassName} from '#/utils/utils';
import * as Tabs from '@radix-ui/react-tabs';
import {ComponentProps, createContext, FunctionComponent, useContext} from 'react';

interface ActionMenuTabsContextValue {
  tab: string;
  queryParamTabKey: string;
  tabKeys: Record<string, string>;
  data: {
    value: string;
    name: string;
    IconComponent: FunctionComponent<ClassName>;
    iconColorClassName?: string;
    ContentComponent: FunctionComponent;
  }[];
}

const initialValue: ActionMenuTabsContextValue = {} as ActionMenuTabsContextValue;
export const ActionMenuTabsContext = createContext<ActionMenuTabsContextValue>(initialValue);

export const ActionMenuControlledTabsRoot: FunctionComponent<
  {
    queryParamTabKey: keyof SLQueryParamConfig;
    tabKeys: Record<string, string>;
    data: ActionMenuTabsContextValue['data'];
    children: React.ReactNode;
  } & ComponentProps<typeof Tabs.Root>
> = ({queryParamTabKey, tabKeys, data, children, ...rest}) => {
  const {tab, setTab} = useQueryParamsTabKey({queryParamTabKey, tabKeys});

  return (
    <Tabs.Root
      className='flex flex-col items-stretch w-full h-full gap-3'
      value={tab}
      onValueChange={setTab}
      {...(rest as any)}
    >
      <ActionMenuTabsContext.Provider value={{tab, queryParamTabKey, tabKeys, data}}>
        {children}
      </ActionMenuTabsContext.Provider>
    </Tabs.Root>
  );
};

export const ActionMenuControlledTabsList: FunctionComponent<ComponentProps<typeof Tabs.List>> = () => {
  const {tab, data, tabKeys} = useContext(ActionMenuTabsContext);

  return (
    <Tabs.List className='flex w-full bg-graybluedark relative rounded-full' {...({} as any)}>
      {data.map(({value, name, IconComponent, iconColorClassName}) => (
        <Tabs.Trigger
          key={`tab-trigger-${value}`}
          className='flex relative justify-center items-center gap-1 z-10 w-1/2 p-2 sm:px-4 py-4 border-0 rounded-full uppercase text-xs text-white font-semibold cursor-pointer outline-none'
          value={value}
          {...({} as any)}
        >
          <IconComponent className={`w-4 aspect-square ${iconColorClassName ?? 'fill-white stroke-transparent'}`} />
          {name}
        </Tabs.Trigger>
      ))}

      <div
        style={{
          width: `${100 / Object.values(tabKeys).length}%`,
          transform: `translateX(${Object.values(tabKeys).indexOf(tab) * 100}%)`,
        }}
        className='absolute top-0 h-full p-1 z-0 transition-transform'
      >
        <div className='w-full h-full bg-deepblue rounded-full' />
      </div>
    </Tabs.List>
  );
};

export const ActionMenuControlledTabsContent: FunctionComponent = () => {
  const {data} = useContext(ActionMenuTabsContext);

  return (
    <>
      {data.map(({value, ContentComponent}) => (
        <Tabs.Content
          key={`tab-content-${value}`}
          className='space-y-3 grow overflow-y-scroll'
          value={value}
          {...({} as any)}
        >
          <ContentComponent />
        </Tabs.Content>
      ))}
    </>
  );
};
